import React, { useEffect, useState } from "react";
import "./QuestionModal.css";
import { DeleteIcon } from "../../../../icons/reactIcons";
import { QuestionCategory } from "../../../../apis/questionApis";

/* ---------- Types ---------- */

interface OptionForm {
  id?: string;
  optionName: string;
  isActive: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  submitting?: boolean;
  categories: QuestionCategory[];
  initialData?: any;
}

/* ---------- Component ---------- */

const EditQuestionModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  categories,
  initialData,
}) => {
  const isCreate = !initialData;

  const [questionName, setQuestionName] = useState("");
  const [questionCategoryId, setQuestionCategoryId] = useState("");
  const [options, setOptions] = useState<OptionForm[]>([
    { optionName: "", isActive: true },
  ]);

  const [errors, setErrors] = useState({
    questionName: false,
    questionCategoryId: false,
    options: [] as boolean[],
  });

  /* ---------- Populate Edit Data ---------- */

  useEffect(() => {
    if (initialData) {
      setQuestionName(initialData.questionName);
      setQuestionCategoryId(initialData.questionCategoryId);

      setOptions(
        initialData.options.map((o: any) => ({
          id: o.id,
          optionName: o.optionName,
          isActive: o.isActive ?? true,
        })),
      );
    } else {
      setQuestionName("");
      setQuestionCategoryId("");
      setOptions([{ optionName: "", isActive: true }]);
    }
  }, [initialData, open]);

  if (!open) return null;

  /* ---------- Helpers ---------- */

  const activeOptions = options.filter((opt) => opt.isActive);

  /* ---------- Option Handlers ---------- */

  const addOption = () => {
    const lastActive = activeOptions[activeOptions.length - 1];

    if (!lastActive?.optionName.trim()) {
      const lastIndex = options.findIndex((o) => o === lastActive);

      setErrors((prev) => ({
        ...prev,
        options: options.map((_, i) =>
          i === lastIndex ? true : prev.options[i],
        ),
      }));
      return;
    }

    setOptions((prev) => [...prev, { optionName: "", isActive: true }]);
  };

  const updateOption = (index: number, value: string) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, optionName: value } : opt)),
    );

    setErrors((prev) => ({
      ...prev,
      options: prev.options.map((err, i) => (i === index ? false : err)),
    }));
  };

  const removeOption = (index: number) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, isActive: false } : opt)),
    );
  };

  /* ---------- Submit ---------- */

  const handleSubmit = () => {
    let hasError = false;

    const optionErrors = options.map((o) => {
      if (o.isActive && !o.optionName.trim()) {
        hasError = true;
        return true;
      }
      return false;
    });

    const newErrors = {
      questionName: !questionName.trim(),
      questionCategoryId: !questionCategoryId,
      options: optionErrors,
    };

    if (
      newErrors.questionName ||
      newErrors.questionCategoryId ||
      optionErrors.includes(true)
    ) {
      setErrors(newErrors);
      return;
    }

    const finalOptions = isCreate ? options.filter((o) => o.isActive) : options;

    onSubmit({
      questionName,
      questionDescription: "",
      questionCategoryId,
      options: finalOptions.map((o, i) => ({
        id: o.id,
        optionName: o.optionName,
        optionOrder: i + 1,
        isActive: o.isActive,
      })),
    });
  };

  const isFormValid =
    questionName.trim() &&
    questionCategoryId &&
    activeOptions.length > 0 &&
    activeOptions.every((opt) => opt.optionName.trim());

  /* ---------- UI ---------- */

  return (
    <div className="questionModal-overlay">
      <div className="questionModal-container large">
        <h3>{isCreate ? "Create Question" : "Edit Question"}</h3>

        <div className="create-questionModal-body">
          {/* Question */}
          <label>
            Question <span className="required">*</span>
          </label>
          <div>
            <textarea
              className={`input ${errors.questionName ? "input-error" : ""}`}
              value={questionName}
              maxLength={200}
              onChange={(e) => {
                setQuestionName(e.target.value);
                setErrors((prev) => ({ ...prev, questionName: false }));
              }}
            />
            <p className="questionModal-questionLength">
              {questionName.length}/200
            </p>
          </div>

          {/* Category */}
          <label>
            Category <span className="required">*</span>
          </label>
          <select
            className={`input ${
              errors.questionCategoryId ? "input-error" : ""
            }`}
            value={questionCategoryId}
            onChange={(e) => {
              setQuestionCategoryId(e.target.value);
              setErrors((prev) => ({
                ...prev,
                questionCategoryId: false,
              }));
            }}
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Options */}
          {options.map((opt, index) =>
            opt.isActive ? (
              <div key={opt.id ?? index}>
                <label className="answer-row-label">
                  Answer Option {activeOptions.findIndex((o) => o === opt) + 1}{" "}
                  <span className="required">*</span>
                </label>

                <div className="answer-row">
                  <input
                    className={`input ${
                      errors.options[index] ? "input-error" : "option-input"
                    }`}
                    placeholder="Enter Answer"
                    value={opt.optionName}
                    onChange={(e) => updateOption(index, e.target.value)}
                    maxLength={50}
                  />

                  <button
                    className="icon-btn delete"
                    onClick={() => removeOption(index)}
                    disabled={activeOptions.findIndex((o) => o === opt) === 0}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ) : null,
          )}

          <button className="add-more" onClick={addOption}>
            + Add more option
          </button>
        </div>

        {/* Footer */}
        <div className="questionModal-footer">
          <button onClick={onClose} className="btn cancel">
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="btn update"
            disabled={submitting || !isFormValid}
          >
            {isCreate ? "Create" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditQuestionModal;
